import { useEffect, useRef, useState } from 'react';
import { CaretDown, X } from '@phosphor-icons/react';
import { changeOwnPassword } from './api';
import { validateNewPassword } from './password';
import { useSession } from './AuthProvider';
import { roleLabel } from './roles';
import { FormErrorSummary } from './FormErrorSummary';
import {
  operationIssue,
  passwordIssue,
  firstConstraintIssue,
  errorDescription,
  type FormIssue,
} from './form-feedback';

export function AccountMenu({
  onNavigate,
  onLogout,
}: {
  onNavigate?: (path: string) => void;
  onLogout?: () => void;
}) {
  const { user, logout, endSession } = useSession();
  const [panel, setPanel] = useState(false);
  const [error, setError] = useState<FormIssue | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const passwordTrigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const element = dialog.current;
    if (panel && element && !element.open) element.showModal();
    if (!panel && element?.open) element.close();
    return () => {
      if (element?.open) element.close();
    };
  }, [panel]);
  function close() {
    setPanel(false);
    passwordTrigger.current?.focus();
  }
  async function change(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    setError(null);
    const constraint = firstConstraintIssue(form);
    if (constraint) {
      setError(constraint);
      return;
    }
    const issue = passwordIssue(data.get('newPassword'), 'own-new-password');
    if (issue) {
      setError(issue);
      return;
    }
    setBusy(true);
    try {
      await changeOwnPassword({
        currentPassword: String(data.get('currentPassword') || ''),
        newPassword: validateNewPassword(data.get('newPassword')),
      });
      form.reset();
      endSession('Đã đổi mật khẩu. Vui lòng đăng nhập lại bằng mật khẩu mới.');
    } catch (e) {
      setError(operationIssue(e, 'Chưa đổi được mật khẩu. Kiểm tra mật khẩu hiện tại và thử lại.'));
    } finally {
      setBusy(false);
    }
  }
  if (!user)
    return (
      <a
        className="button surface"
        href="/login"
        onClick={(event) => {
          if (onNavigate) {
            event.preventDefault();
            onNavigate('/login');
          }
        }}
      >
        Đăng nhập
      </a>
    );
  return (
    <>
      <details className="account-menu">
        <summary>
          <span>
            {user.displayName || user.username}
            <small>{roleLabel(user.role)}</small>
          </span>
          <CaretDown size={14} aria-hidden="true" />
        </summary>
        <nav aria-label="Tài khoản cá nhân">
          <button
            ref={passwordTrigger}
            onClick={() => {
              setPanel(true);
              setError(null);
              setShowPassword(false);
            }}
          >
            Đổi mật khẩu
          </button>
          <a
            href="/admin"
            onClick={(event) => {
              if (onNavigate) {
                event.preventDefault();
                onNavigate('/admin');
              }
              const menu = event.currentTarget.closest('details');
              if (menu) menu.open = false;
            }}
          >
            Quản lý công trình
          </a>
          <a
            href="/admin/models"
            onClick={(event) => {
              if (onNavigate) {
                event.preventDefault();
                onNavigate('/admin/models');
              }
              const menu = event.currentTarget.closest('details');
              if (menu) menu.open = false;
            }}
          >
            Quản lý dữ liệu BIM / GIS
          </a>
          {user.role === 'admin' && (
            <a
              href="/admin/users"
              onClick={(event) => {
                if (onNavigate) {
                  event.preventDefault();
                  onNavigate('/admin/users');
                }
                const menu = event.currentTarget.closest('details');
                if (menu) menu.open = false;
              }}
            >
              Quản trị hệ thống
            </a>
          )}
          <button
            onClick={() => {
              if (onLogout) onLogout();
              else void logout();
            }}
          >
            Đăng xuất
          </button>
        </nav>
      </details>
      <dialog
        ref={dialog}
        className="admin-dialog password-dialog"
        aria-labelledby="password-dialog-title"
        aria-describedby="password-dialog-help"
        onCancel={(event) => {
          if (busy) event.preventDefault();
          else close();
        }}
        onClose={() => setPanel(false)}
      >
        <header>
          <h2 id="password-dialog-title">Đổi mật khẩu</h2>
          <button
            className="icon-button"
            type="button"
            disabled={busy}
            onClick={close}
            aria-label="Đóng cửa sổ đổi mật khẩu"
          >
            <X size={20} />
          </button>
        </header>
        <form
          noValidate
          className="account-form"
          onSubmit={change}
          aria-busy={busy}
          aria-describedby={error ? 'own-password-error' : undefined}
        >
          <p id="password-dialog-help">
            Sau khi lưu, mọi phiên đăng nhập của bạn sẽ kết thúc. Bạn cần đăng nhập lại bằng mật
            khẩu mới.
          </p>
          <fieldset disabled={busy} className="account-form">
            <label>
              Mật khẩu hiện tại
              <input
                id="own-current-password"
                name="currentPassword"
                data-field-label="Mật khẩu hiện tại"
                aria-invalid={error?.field === 'own-current-password' || undefined}
                aria-describedby={errorDescription(
                  undefined,
                  error,
                  'own-password-error',
                  'own-current-password',
                )}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                autoFocus
              />
            </label>
            <label>
              Mật khẩu mới
              <input
                id="own-new-password"
                name="newPassword"
                data-field-label="Mật khẩu mới"
                aria-invalid={error?.field === 'own-new-password' || undefined}
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                aria-describedby={errorDescription(
                  'own-password-help',
                  error,
                  'own-password-error',
                  'own-new-password',
                )}
              />
              <small id="own-password-help">
                Khuyến nghị dùng từ 12 ký tự, kết hợp chữ, số hoặc ký hiệu.
              </small>
            </label>
            <label className="account-checkbox">
              <input
                type="checkbox"
                checked={showPassword}
                onChange={(e) => setShowPassword(e.target.checked)}
              />
              Hiện mật khẩu
            </label>
          </fieldset>
          <FormErrorSummary issue={error} id="own-password-error" />
          <div className="admin-actions">
            <button className="button primary" disabled={busy}>
              {busy ? 'Đang lưu…' : 'Lưu mật khẩu mới'}
            </button>
            <button className="button surface" type="button" disabled={busy} onClick={close}>
              Hủy
            </button>
          </div>
        </form>
      </dialog>
    </>
  );
}
